/* eslint-disable no-nested-ternary */
import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import path from 'path-browserify';
import { get, remove, map, isArray } from 'lodash';
import {
  MessageBar,
  MessageBarType,
  Spinner,
  SpinnerSize,
  TooltipHost,
} from 'office-ui-fabric-react';
import { ActionButton, PrimaryButton, IconButton } from 'office-ui-fabric-react/lib/Button';
import { Checkbox } from 'office-ui-fabric-react/lib/Checkbox';
import { FontIcon } from 'office-ui-fabric-react/lib/Icon';
import { DialogType } from 'office-ui-fabric-react/lib/Dialog';
import { getFileTypeIconProps } from '@uifabric/file-type-icons';
import { Text } from 'office-ui-fabric-react/lib/Text';
import Module from '../../components/Module';
import ModuleContent from '../../components/ModuleContent';
import SubModule from '../../components/SubModule';
import SubModuleHeader from '../../components/SubModuleHeader';
import SubModuleContent from '../../components/SubModuleContent';
import DocumentUpload from '../../components/DocumentUpload';
import DetailsList from '../../components/DetailsList';
import { uploadAttachment, fetchAttachment, deleteAttachment } from '../../actions/attachments';
import { uploadContractAttachment } from '../../actions/contracts';
import { showFlashMessage } from '../../actions/ui';
import TextButton from '../../components/TextButton';
import { can, hasRole, getUser } from '../../utils/auth';
import { formatDateString, pxToRem } from '../../utils';
import ReviewerSelectionDialog from '../../components/ReviewerSelectionDialog';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import PendingAttachmentApprovals from '../../components/PendingAttachmentApprovals';
import PendingRequestApprovals from '../../components/PendingRequestApprovals';
import DetailsListOverflowTooltip from '../../components/DetailsListOverflowTooltip';
import styles from './ContractAttachments.style';

const rejectConfirmation = {
  type: 'reject',
  content: 'Are you sure you want to reject this attachment for partner visibility?',
  button: 'Reject',
};

const approveConfirmation = {
  type: 'approve',
  content: 'Are you sure you want to share this attachment for partner visibility?',
  button: 'Approve',
};

const changeConfirmation = {
  content: 'Are your sure you want to change the Attachment view from Partner to Internal.',
  button: 'Confirm',
  type: 'viewChange',
};

const removeConfirmation = {
  type: 'remove',
  content: 'Are you sure you want to remove the attachment?',
  button: 'Remove',
};

const ContractAttachments = () => {
  const { contractAddress } = useParams();
  const uploadref = useRef(null);
  const dispatch = useDispatch();
  const user = getUser();

  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const [dialogContent, setDialogContent] = useState(null);
  const [reviewAttachmentIds, setReviewAttachmentIds] = useState([]);
  const [showReviewerSelectionDialog, setShowReviewerSelectionDialog] = useState(false);
  const [isRejectClick, setIsRejectClick] = useState(false);

  const attachments = useSelector(state =>
    get(state, `entities.contracts.${contractAddress}.attachments`, []),
  );

  let error = null;
  let isApprover = false;
  let pendingApprovals = [];
  let allOpenRequests = [];
  const viewChange = {};

  const isPartner = hasRole('partner');
  const isInternal = hasRole('msft');

  const contractAttachments = isPartner
    ? attachments.filter(attachment => attachment.isPartnerVisible)
    : attachments.filter(attachment => attachment.approvalStatus !== 'PendingApproval');

  const filteredPending = attachments.filter(
    attachment => attachment.approvalStatus === 'PendingApproval',
  );
  if (filteredPending.length) {
    isApprover = filteredPending.some(item => item.reviewer?.oid === user.profile?.oid);
    pendingApprovals = filteredPending.filter(
      attachment => attachment.reviewer?.oid === user.profile?.oid,
    );
    allOpenRequests = filteredPending;
  }

  const canManageContractAttachment = can('manageContractAttachments');

  const handleSubmitApproval = async formValue => {
    const updatedAttachments = attachments.filter(attachment =>
      reviewAttachmentIds.includes(attachment.attachmentId),
    );

    const attachmentValues = updatedAttachments.map(attachment => {
      return {
        ...attachment,
        approvalStatus: 'PendingApproval',
        requestor: user.profile?.oid,
        reviewer: formValue.Id,
        createdBy: attachment?.createdBy?.oid,
        comment: formValue.comment,
      };
    });

    setIsLoading(true);
    const response = await dispatch(
      uploadContractAttachment(contractAddress, {
        activity: 'submit-for-approval',
        attachmentValues,
      }),
    );
    if (response.error && response.payload.message) {
      setIsLoading(false);
      error = new Error(response.payload.message);
      error.payload = response.payload.message;
      dispatch(
        showFlashMessage(
          response.payload?.message || 'Error while submitting attachments for approval.',
          {
            type: 'error',
            duration: 10000,
          },
        ),
      );
    } else {
      setReviewAttachmentIds([]);
      dispatch(
        showFlashMessage('Successfully submitted attachments for the approval.', {
          type: 'success',
          duration: 10000,
        }),
      );
    }
    setIsLoading(false);
    setShowReviewerSelectionDialog(false);
  };

  const handlePartnerVisibleChange = async (item, e) => {
    const statePrev = [...reviewAttachmentIds];

    if (!e && item.isPartnerVisible) {
      setDialogContent({ ...changeConfirmation, itemId: item.attachmentId });
      setIsDialogVisible(true);
    } else if (!e && !item.isPartnerVisible) {
      remove(statePrev, id => id === item.attachmentId);
    } else {
      const updatedID = get(
        attachments.find(
          attachment => attachment.attachmentId === item.attachmentId && !item.isPartnerVisible,
        ),
        'attachmentId',
        '',
      );
      if (updatedID) {
        statePrev.push(updatedID);
      }
    }
    setReviewAttachmentIds(statePrev);
  };

  const handleSubmit = () => {
    setShowReviewerSelectionDialog(true);
  };

  const onDismissDialog = () => {
    setShowReviewerSelectionDialog(false);
  };

  const handleDownload = async item => {
    if (isArray(item)) {
      const downloadAttachments = attachments.filter(attachment =>
        item.includes(attachment.attachmentId),
      );
      await Promise.all(
        downloadAttachments.map(async eachItem => {
          const file = {
            documentName: eachItem.displayName,
            attachmentId: eachItem.attachmentId,
            documentType: 'attachment',
          };
          await dispatch(fetchAttachment(contractAddress, file));
        }),
      );
    } else {
      const file = {
        documentName: item.displayName,
        attachmentId: item.attachmentId,
        documentType: 'attachment',
      };
      await dispatch(fetchAttachment(contractAddress, file));
    }
  };

  const handleRemove = async () => {
    setIsLoading(true);
    const item = attachments.find(attachment => attachment.attachmentId === dialogContent?.itemId);
    const file = {
      documentName: item.displayName,
      attachmentId: item.attachmentId,
      documentType: 'attachment',
    };

    const attachmentResponse = await dispatch(deleteAttachment(contractAddress, file));

    if (attachmentResponse.error) {
      setIsLoading(false);
      dispatch(
        showFlashMessage(attachmentResponse?.payload.message || 'Error deleting file', {
          type: 'error',
          duration: 10000,
        }),
      );
    } else {
      item.reviewer = get(item, 'reviewer.oid');
      item.requestor = get(item, 'requestor.oid');
      item.createdBy = get(item, 'createdBy.oid');
      const response = await dispatch(
        uploadContractAttachment(contractAddress, {
          activity: 'removed',
          attachmentValues: [item],
        }),
      );

      if (response.error && response.payload.message) {
        setIsLoading(false);
        error = new Error(response.payload.message);
        error.payload = response.payload.message;
        dispatch(
          showFlashMessage(response.payload?.message || 'Error uploading file', {
            type: 'error',
            duration: 10000,
          }),
        );
      } else {
        dispatch(
          showFlashMessage('Successfully deleted attachment.', {
            type: 'success',
            duration: 10000,
          }),
        );
      }
    }
    setIsLoading(false);
  };

  const handleUpload = async files => {
    if (files.length === 0) {
      return;
    }
    setIsUploading(true);
    const attachmentResponse = await dispatch(
      uploadAttachment(files, contractAddress, 'attachment'),
    );

    if (attachmentResponse.error) {
      dispatch(
        showFlashMessage(attachmentResponse?.payload.message || 'Error uploading file', {
          type: 'error',
          duration: 10000,
        }),
      );
    } else {
      const attachmentValues = [
        {
          ...attachmentResponse?.payload.files[0],
          displayName: files[0].name,
          isPartnerVisible: false,
        },
      ];

      const response = await dispatch(
        uploadContractAttachment(contractAddress, { activity: 'uploaded', attachmentValues }),
      );

      if (response.error && response.payload.message) {
        setIsLoading(false);
        error = new Error(response.payload.message);
        error.payload = response.payload.message;
        dispatch(
          showFlashMessage(response.payload?.message || 'Error uploading file', {
            type: 'error',
            duration: 10000,
          }),
        );
      } else {
        dispatch(
          showFlashMessage('Successfully uploaded attachment.', {
            type: 'success',
            duration: 10000,
          }),
        );
      }
    }
    setIsUploading(false);
  };

  const handleApprove = attachmentId => {
    const updatedContent = { ...approveConfirmation, itemId: attachmentId };
    setDialogContent(updatedContent);
    setIsDialogVisible(true);
  };

  const handleReject = attachmentId => {
    const updatedContent = { ...rejectConfirmation, itemId: attachmentId };
    setIsRejectClick(true);
    setDialogContent(updatedContent);
    setIsDialogVisible(true);
  };

  const onRemoveClick = attachmentId => {
    const updatedContent = { ...removeConfirmation, itemId: attachmentId };
    setDialogContent(updatedContent);
    setIsDialogVisible(true);
  };

  const onApprove = async attachmentId => {
    setIsLoading(true);
    let updatedAttachments = [];
    if (isArray(attachmentId)) {
      const filteredAttachment = attachments.filter(attachment =>
        attachmentId.includes(attachment.attachmentId),
      );
      updatedAttachments = filteredAttachment.map(item => {
        return {
          ...item,
          approvalStatus: 'Approved',
          isPartnerVisible: true,
          reviewer: get(item, 'reviewer.oid'),
          requestor: get(item, 'requestor.oid'),
          createdBy: get(item, 'createdBy.oid'),
        };
      });
    } else {
      const filteredAttachment = attachments.find(
        attachment => attachment.attachmentId === attachmentId,
      );
      filteredAttachment.approvalStatus = 'Approved';
      filteredAttachment.isPartnerVisible = true;
      filteredAttachment.reviewer = get(filteredAttachment, 'reviewer.oid');
      filteredAttachment.requestor = get(filteredAttachment, 'requestor.oid');
      filteredAttachment.createdBy = get(filteredAttachment, 'createdBy.oid');
      updatedAttachments.push(filteredAttachment);
    }
    if (updatedAttachments.length) {
      const response = await dispatch(
        uploadContractAttachment(contractAddress, {
          activity: 'approve',
          attachmentValues: updatedAttachments,
        }),
      );

      if (response.error && response.payload.message) {
        error = new Error(response.payload.message);
        error.payload = response.payload.message;
        dispatch(
          showFlashMessage(response.payload?.message || 'Error while approving', {
            type: 'error',
            duration: 10000,
          }),
        );
      } else {
        dispatch(
          showFlashMessage('Attachments successfully reviewed and approved', {
            type: 'success',
            duration: 10000,
          }),
        );
      }
    }
    setIsLoading(false);
  };

  const onReject = async (attachmentId, formValue) => {
    setIsLoading(true);
    let updatedAttachments = [];
    if (isArray(attachmentId)) {
      const filteredAttachment = attachments.filter(attachment =>
        attachmentId.includes(attachment.attachmentId),
      );
      updatedAttachments = filteredAttachment.map(item => {
        return {
          ...item,
          approvalStatus: 'Rejected',
          reviewer: get(item, 'reviewer.oid'),
          requestor: get(item, 'requestor.oid'),
          createdBy: get(item, 'createdBy.oid'),
          isPartnerVisible: false,
          rejectComment: formValue?.comment || '',
        };
      });
    } else {
      const filteredAttachment = attachments.find(
        attachment => attachment.attachmentId === attachmentId,
      );
      filteredAttachment.approvalStatus = 'Rejected';
      filteredAttachment.reviewer = get(filteredAttachment, 'reviewer.oid');
      filteredAttachment.requestor = get(filteredAttachment, 'requestor.oid');
      filteredAttachment.createdBy = get(filteredAttachment, 'createdBy.oid');
      filteredAttachment.isPartnerVisible = false;
      filteredAttachment.rejectComment = formValue?.comment || '';
      updatedAttachments.push(filteredAttachment);
    }
    if (updatedAttachments.length) {
      const response = await dispatch(
        uploadContractAttachment(contractAddress, {
          activity: 'reject',
          attachmentValues: updatedAttachments,
        }),
      );

      if (response.error && response.payload.message) {
        error = new Error(response.payload.message);
        error.payload = response.payload.message;
        dispatch(
          showFlashMessage(response.payload?.message || 'Error while rejecting', {
            type: 'error',
            duration: 10000,
          }),
        );
      } else {
        dispatch(
          showFlashMessage('Attachments reviewed and rejected', {
            type: 'error',
            duration: 10000,
          }),
        );
      }
    }
    setIsLoading(false);
  };

  const onViewChange = async itemId => {
    const statePrev = [...reviewAttachmentIds];
    remove(statePrev, id => id === itemId);
    const attachmentValue = attachments.find(attachment => attachment.attachmentId === itemId);
    if (attachmentValue.isPartnerVisible) {
      attachmentValue.isPartnerVisible = false;
      attachmentValue.reviewer = get(attachmentValue, 'reviewer.oid');
      attachmentValue.requestor = get(attachmentValue, 'requestor.oid');
      attachmentValue.createdBy = get(attachmentValue, 'createdBy.oid');
      setIsLoading(true);
      const reqData = { activity: 'updated', attachmentValues: [attachmentValue] };
      await dispatch(uploadContractAttachment(contractAddress, reqData));
      setIsLoading(false);
    }
    setReviewAttachmentIds(statePrev);
  };

  const handleDialogSubmit = async (formValue = {}) => {
    switch (dialogContent.type) {
      case 'remove':
        await handleRemove();
        break;
      case 'reject':
        await onReject(dialogContent?.itemId, formValue);
        break;
      case 'approve':
        await onApprove(dialogContent?.itemId);
        break;
      case 'viewChange':
        await onViewChange(dialogContent?.itemId);
        break;
      default:
        break;
    }
    setIsDialogVisible(false);
    setIsRejectClick(false);
  };

  const usersUploadedAttachments = map(
    attachments.filter(attachment => reviewAttachmentIds.includes(attachment.attachmentId)) || [],
    'createdBy.oid',
  );

  let columns = [
    {
      key: 'fileIcon',
      name: 'File Type',
      iconName: 'Page',
      isIconOnly: true,
      headerClassName: styles.iconColumnHeader,
      iconClassName: styles.headerIcon,
      columnActionsMode: 0,
      minWidth: 16,
      maxWidth: 16,
      onRender(attachment) {
        const extension = path.extname(attachment.displayName);

        return (
          <FontIcon {...getFileTypeIconProps({ extension, size: 16, imageFileType: 'png' })} />
        );
      },
    },
    {
      key: 'name',
      name: 'File Name',
      fieldName: 'displayName',
      columnActionsMode: 0,
      minWidth: 180,
      maxWidth: 180,
      isPadded: true,
    },
    {
      key: 'lastUpdatedTimestamp',
      name: 'Last Modified',
      fieldName: 'lastUpdatedTimestamp',
      columnActionsMode: 0,
      minWidth: 60,
      maxWidth: 60,
      isPadded: true,
      onRender: item => (
        <DetailsListOverflowTooltip
          ariaLabel="last modified"
          displayContent={formatDateString(item.lastUpdatedTimestamp)}
        />
      ),
    },
    {
      key: 'createdBy',
      name: 'Owner',
      fieldName: 'owner',
      columnActionsMode: 0,
      minWidth: 130,
      maxWidth: 130,
      isPadded: true,
      onRender: item => (
        <div style={{ width: '100%' }}>
          <DetailsListOverflowTooltip
            ariaLabel="owner"
            displayContent={item.createdBy?.displayName || item.createdBy?.oid}
            tooltipContent={item.createdBy?.displayName || item.createdBy?.oid}
          />
          <br /> {formatDateString(item.createdTimestamp)}
        </div>
      ),
    },
    {
      key: 'view',
      name: 'View',
      fieldName: 'view',
      ariaLabel: 'checkbox',
      isResizable: false,
      columnActionsMode: 0,
      minWidth: 50,
      maxWidth: 50,
      onRender: item => {
        if (canManageContractAttachment) {
          const isChecked =
            item.isPartnerVisible || reviewAttachmentIds.some(id => id === item.attachmentId);
          const isUpdating =
            viewChange && viewChange.id === item.attachmentId ? viewChange.isUpdating : false;
          return (
            <>
              <Checkbox
                ariaLabel="isPartnerVisible Checkbox"
                field={{ name: 'view', value: isChecked }}
                label=""
                onChange={event => handlePartnerVisibleChange(item, event.target.checked)}
                checked={isChecked}
                disabled={isUpdating}
              />
              {isChecked ? 'Partner' : 'Internal'}
              {isUpdating && <Spinner className={styles.loadingSpinner} size={SpinnerSize.small} />}
            </>
          );
        }
        return item.isPartnerVisible ? 'Partner' : 'Internal';
      },
    },
    {
      key: 'rejectComment',
      name: 'Comment',
      fieldName: 'rejectComment',
      minWidth: 30,
      maxWidth: 30,
      columnActionsMode: 0,
      isPadded: true,
      className: styles.alignCenter,
      onRender(attachment) {
        if (attachment.rejectComment && attachment.approvalStatus === 'Rejected') {
          return (
            <TooltipHost
              content={attachment.rejectComment}
              calloutProps={{
                gapSpace: 0,
                target: `#select-comment-${attachment.attachmentId}`,
              }}
              styles={{
                root: {
                  display: 'flex',
                },
              }}
            >
              <IconButton
                iconProps={{ iconName: 'CannedChat', style: { fontSize: pxToRem(14) } }}
                ariaLabel="Comment"
                id={`select-comment-${attachment.attachmentId}`}
                className={styles.infoMsgIcon}
              />
            </TooltipHost>
          );
        }
        return '';
      },
    },
    {
      key: 'remove',
      name: 'Remove',
      arianame: 'remove',
      isIconOnly: true,
      fieldName: 'remove',
      columnActionsMode: 0,
      minWidth: 50,
      maxWidth: 50,
      isPadded: true,
      onRender: item => {
        return (
          <TextButton
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
              onRemoveClick(item.attachmentId);
            }}
          >
            Remove
          </TextButton>
        );
      },
    },
    {
      key: 'download',
      name: 'Download',
      arianame: 'download',
      isIconOnly: true,
      columnActionsMode: 0,
      fieldName: 'download',
      headerClassName: styles.iconColumnHeader,
      minWidth: 50,
      maxWidth: 50,
      isPadded: true,
      onRender: item => {
        return (
          <TextButton
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
              handleDownload(item);
            }}
          >
            Download
          </TextButton>
        );
      },
    },
  ];

  columns = isInternal ? columns : columns.filter(c => c.key !== 'rejectComment');
  const partnerColumns = () => {
    const partnerViewColumns = columns.filter(
      column => column.key !== 'remove' && column.key !== 'view',
    );
    return partnerViewColumns.map(column => {
      const item =
        column.key === 'name' ? { ...column, minWidth: 530, maxWidth: 530 } : { ...column };
      return item;
    });
  };

  const viewAttachmentColumns = () => {
    const viewColumns = columns.filter(column => column.key !== 'remove');
    return viewColumns.map(column => {
      const item =
        column.key === 'view' ? { ...column, minWidth: 150, maxWidth: 150 } : { ...column };
      return item;
    });
  };

  const attachmentColumns = canManageContractAttachment
    ? columns
    : isPartner
    ? partnerColumns()
    : viewAttachmentColumns();

  const renderAddAttachment = () => {
    if (canManageContractAttachment) {
      return (
        <div className={styles.addAttachmentButton}>
          <ActionButton
            iconProps={{ iconName: 'Attach' }}
            title="Attach"
            ariaLabel="Attach"
            onClick={() => uploadref.current.click()}
          >
            Add Attachment
            {isUploading && <Spinner className={styles.loadingSpinner} size={SpinnerSize.medium} />}
          </ActionButton>
          <DocumentUpload
            className={styles.uploader}
            ref={uploadref}
            onUpload={handleUpload}
            accept=".*"
            id="btn_statementAttachmentUpload"
          />
          <div className={styles.submitButton}>
            <PrimaryButton onClick={handleSubmit} disabled={!reviewAttachmentIds?.length}>
              Submit for Review
            </PrimaryButton>
          </div>
          <div>
            <FontIcon iconName="Info" ariaLabel="Info" className={styles.uploadInfoIcon} />
            <Text as="p" variant="small" className={styles.uploadInfoText}>
              Acceptable attachments: .csv, .doc, .docx, .jpeg, .jpg, .txt, .xls, .xlsm, .xlsx
            </Text>
          </div>
        </div>
      );
    }

    return '';
  };

  const renderPendingApprovalModule = () => {
    if (!isPartner && isApprover && canManageContractAttachment && pendingApprovals?.length) {
      return (
        <PendingAttachmentApprovals
          pendingAttachments={pendingApprovals}
          onDownload={handleDownload}
          onRemove={onRemoveClick}
          onApproveClick={handleApprove}
          onRejectClick={handleReject}
          onApproveAllSelected={onApprove}
          onRejectAllSelected={onReject}
          isLoading={isLoading}
        />
      );
    }

    return '';
  };

  const renderAllOpenRequestsModule = () => {
    if (!isPartner && canManageContractAttachment && allOpenRequests?.length) {
      return (
        <PendingRequestApprovals
          pendingAttachments={allOpenRequests}
          onDownload={handleDownload}
          onRemove={onRemoveClick}
        />
      );
    }

    return '';
  };

  return (
    <>
      {error && (
        <MessageBar
          isMultiline={false}
          messageBarType={MessageBarType.error}
          dismissButtonAriaLabel="Close"
        >
          {error.name}: Try uploading the document again.
        </MessageBar>
      )}
      <Module>
        <ModuleContent>
          <SubModule>
            <SubModuleHeader id="attachmentsHeading">All</SubModuleHeader>
            <SubModuleContent>
              {contractAttachments && contractAttachments.length > 0 ? (
                <DetailsList columns={attachmentColumns} items={contractAttachments} multiLine />
              ) : (
                <div className={styles.attachmentMsg}>
                  <FontIcon className={styles.infoIcon} iconName="Info" arianame="Info" />
                  No attachments currently added to this contract.
                </div>
              )}
              {renderAddAttachment()}
            </SubModuleContent>
          </SubModule>
        </ModuleContent>
      </Module>
      {renderPendingApprovalModule()}
      {renderAllOpenRequestsModule()}
      {showReviewerSelectionDialog && (
        <ReviewerSelectionDialog
          contractAddress={contractAddress}
          usersUploadedAttachments={usersUploadedAttachments}
          onSubmit={handleSubmitApproval}
          isOpen={showReviewerSelectionDialog}
          onDismiss={onDismissDialog}
        />
      )}
      <ConfirmationDialog
        onDismiss={() => {
          setIsDialogVisible(false);
          setIsRejectClick(false);
        }}
        onSubmit={handleDialogSubmit}
        isLoading={isLoading}
        hidden={!isDialogVisible}
        isRejectClick={isRejectClick}
        dialogContentProps={{
          type: DialogType.close,
          title: 'Confirmation',
          closeButtonAriaLabel: 'Close',
        }}
        dialogBodyContent={{
          content: dialogContent?.content,
          buttonText: dialogContent?.button,
        }}
      />
    </>
  );
};

export default ContractAttachments;
